import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { User, Globe, Flame, Zap, BookOpen, Award, LogOut, CreditCard, Phone, MapPin, Calendar, Edit, Save, X, ChevronRight, CheckCircle, FileText, Shield, Lock, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { Progress } from '../components/ui/progress';
import NotificationPreferencesSection from '../components/profile/NotificationPreferencesSection';
import DijitalRozetConsentSection from '../components/profile/DijitalRozetConsentSection';
import DeleteAccountSection from '../components/profile/DeleteAccountSection';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Deep-link'lenebilir sekmeler — ör. ticari e-postadaki 6563 md.6 opt-out
// linki `/hesap/bildirim-tercihleri` üzerinden `/profile?tab=notifications`e
// yönlendirir (bkz. App.js route yorumu, backend/routes/admin_bulk.py::
// _commercial_email_opt_out_footer). Bilinmeyen/eksik ?tab= değeri 'overview'a
// düşer — rastgele bir string state'e sızmaz.
// 'privacy' sekmesi Dijital Rozet rızasını barındırır; CertificatesPage'deki
// "Onay ver" linki de `/profile?tab=privacy`ye yönlendirir (bkz. o dosyadaki
// awaiting_consent durumu).
const VALID_PROFILE_TABS = ['overview', 'courses', 'payments', 'certificates', 'notifications', 'privacy'];

export default function ProfilePage() {
  const { authHeaders, logout, setUser } = useAuth();
  const { t, lang, toggleLanguage } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState(() => {
    const fromQuery = searchParams.get('tab');
    return VALID_PROFILE_TABS.includes(fromQuery) ? fromQuery : 'overview';
  });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', address: '', city: '', birth_date: '' });
  const [loading, setLoading] = useState(true);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [showPwSection, setShowPwSection] = useState(false);
  const [pwVisibility, setPwVisibility] = useState({ current: false, new: false, confirm: false });

  useEffect(() => {
    axios.get(`${API}/profile`, { headers: authHeaders() })
      .then(res => {
        setProfile(res.data);
        const u = res.data.user || {};
        setForm({ name: u.name || '', phone: u.phone || '', address: u.address || '', city: u.city || '', birth_date: u.birth_date || '' });
      }).catch(console.error).finally(() => setLoading(false));
  }, [authHeaders]);

  const handleSave = async () => {
    try {
      const res = await axios.put(`${API}/profile`, { ...form, preferred_language: lang }, { headers: authHeaders() });
      setUser(res.data);
      setEditing(false);
      // Refresh profile
      const pRes = await axios.get(`${API}/profile`, { headers: authHeaders() });
      setProfile(pRes.data);
    } catch (err) { console.error(err); }
  };

  const downloadCertPdf = async (certId, courseTitle) => {
    try {
      const res = await axios.get(`${API}/certificates/${certId}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const safeName = (courseTitle || certId).replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s-]/g, '').trim().replace(/\s+/g, '-');
      a.download = `sertifika-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      let msg = lang === 'tr' ? 'Sertifika indirilemedi.' : 'Could not download certificate.';
      if (status === 401 || status === 403) {
        msg = lang === 'tr' ? 'Oturum süresi doldu. Lütfen tekrar giriş yapın.' : 'Session expired. Please log in again.';
      } else {
        try {
          const blob = err?.response?.data;
          if (blob instanceof Blob) {
            const text = await blob.text();
            const json = JSON.parse(text);
            msg = (lang === 'tr' ? json?.message_tr : json?.message_en) || json?.detail || msg;
          }
        } catch { /* yoksay */ }
      }
      toast.error(msg);
    }
  };

  if (loading) return (<div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]"><div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" /></div>);

  const u = profile?.user || {};
  const tabs = [
    { id: 'overview', label: lang === 'tr' ? 'Genel' : 'Overview' },
    { id: 'courses', label: lang === 'tr' ? 'Kurslarım' : 'My Courses' },
    { id: 'payments', label: lang === 'tr' ? 'Ödemelerim' : 'Payments' },
    { id: 'certificates', label: lang === 'tr' ? 'Sertifikalar' : 'Certificates' },
    { id: 'notifications', label: t('profile.notifPrefs.tabLabel') },
    { id: 'privacy', label: t('profile.privacyTab.label') },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-app)]" data-testid="profile-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-[var(--brand-primary)] to-blue-700 text-white pt-8 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden border-2 border-white/40">
              {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <User size={36} className="text-white" />}
            </div>
            <div className="flex-1">
              <h1 className="font-display text-2xl font-bold">{u.name}</h1>
              <p className="text-blue-200 text-sm font-medium">{u.email}</p>
              {u.phone && <p className="text-blue-200 text-xs font-medium flex items-center gap-1 mt-0.5"><Phone size={10} /> {u.phone}</p>}
            </div>
            <button onClick={() => setEditing(!editing)} className="px-3 py-1.5 rounded-lg bg-white/20 text-white text-xs font-bold hover:bg-white/30" data-testid="edit-profile-btn">
              <Edit size={14} className="inline mr-1" /> {lang === 'tr' ? 'Düzenle' : 'Edit'}
            </button>
          </div>
          {/* Stats row */}
          <div className="flex gap-4 mt-6">
            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-center"><Flame size={18} className="mx-auto text-orange-300" /><p className="font-bold text-lg">{profile?.streak || 0}</p><p className="text-[9px] text-blue-200">Streak</p></div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-center"><Zap size={18} className="mx-auto text-yellow-300" /><p className="font-bold text-lg">{profile?.total_xp || 0}</p><p className="text-[9px] text-blue-200">XP</p></div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-center"><BookOpen size={18} className="mx-auto text-green-300" /><p className="font-bold text-lg">{profile?.enrolled_count || 0}</p><p className="text-[9px] text-blue-200">{lang === 'tr' ? 'Kurs' : 'Courses'}</p></div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-center"><Award size={18} className="mx-auto text-purple-300" /><p className="font-bold text-lg">{(profile?.certificates || []).length}</p><p className="text-[9px] text-blue-200">{lang === 'tr' ? 'Sertifika' : 'Certs'}</p></div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-center"><Shield size={18} className="mx-auto text-red-300" /><p className="font-bold text-lg">{profile?.quiz_count || 0}</p><p className="text-[9px] text-blue-200">Quiz</p></div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-8">
        {/* Edit Form */}
        {editing && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="card-clay p-5 mb-4 space-y-3">
            <div className="flex justify-between"><h3 className="font-bold text-sm">{lang === 'tr' ? 'Profili Düzenle' : 'Edit Profile'}</h3><button onClick={() => setEditing(false)}><X size={16} /></button></div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Ad Soyad' : 'Full Name'}</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" data-testid="edit-name-input" /></div>
              <div><label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Telefon' : 'Phone'}</label>
              <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" /></div>
              <div><label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Adres' : 'Address'}</label>
              <input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" /></div>
              <div><label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Sehir' : 'City'}</label>
              <input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" /></div>
            </div>
            <button onClick={handleSave} className="btn-success px-6 py-2 text-sm" data-testid="save-profile-btn"><Save size={14} className="inline mr-1" /> {lang === 'tr' ? 'Kaydet' : 'Save'}</button>
          </motion.div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 overflow-x-auto">
          {tabs.map(tb => (
            <button key={tb.id} onClick={() => setTab(tb.id)} className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap ${tab === tb.id ? 'bg-white shadow-md text-[var(--brand-primary)]' : 'text-[var(--text-muted)] hover:bg-white/50'}`} data-testid={`profile-tab-${tb.id}`}>{tb.label}</button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-4">
            {/* Personal Info Card */}
            <div className="card-clay p-5">
              <h3 className="font-bold text-sm mb-3">{lang === 'tr' ? 'Kişisel Bilgiler' : 'Personal Info'}</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><User size={14} className="text-[var(--text-muted)]" /><span className="text-[var(--text-muted)]">{lang === 'tr' ? 'Ad' : 'Name'}:</span><span className="font-bold">{u.name}</span></div>
                <div className="flex items-center gap-2"><Phone size={14} className="text-[var(--text-muted)]" /><span className="text-[var(--text-muted)]">{lang === 'tr' ? 'Tel' : 'Phone'}:</span><span className="font-bold">{u.phone || '-'}</span></div>
                <div className="flex items-center gap-2"><MapPin size={14} className="text-[var(--text-muted)]" /><span className="text-[var(--text-muted)]">{lang === 'tr' ? 'Şehir' : 'City'}:</span><span className="font-bold">{u.city || '-'}</span></div>
                <div className="flex items-center gap-2"><Calendar size={14} className="text-[var(--text-muted)]" /><span className="text-[var(--text-muted)]">{lang === 'tr' ? 'Kayıt' : 'Joined'}:</span><span className="font-bold">{u.created_at ? new Date(u.created_at).toLocaleDateString('tr-TR') : '-'}</span></div>
              </div>
            </div>

            {/* Language + Logout */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="card-clay p-5 flex items-center justify-between">
                <div className="flex items-center gap-2"><Globe size={18} className="text-[var(--brand-primary)]" /><div><p className="font-bold text-sm">{lang === 'tr' ? 'Dil' : 'Language'}</p><p className="text-[10px] text-[var(--text-muted)]">{lang === 'tr' ? 'Türkçe' : 'English'}</p></div></div>
                <button onClick={toggleLanguage} className="btn-tactile px-4 py-2 text-sm bg-white border-[var(--border-light)]" data-testid="profile-language-toggle">{lang === 'tr' ? 'EN' : 'TR'}</button>
              </div>
              <button onClick={async () => { await logout(); navigate('/'); }} className="card-clay p-5 flex items-center gap-3 text-left hover:border-red-300 transition-colors" data-testid="logout-btn">
                <LogOut size={18} className="text-red-500" /><div><p className="font-bold text-sm text-red-600">{t('logout')}</p><p className="text-[10px] text-[var(--text-muted)]">{lang === 'tr' ? 'Hesaptan çıkış yap' : 'Sign out of account'}</p></div>
              </button>
            </div>

            {/* Change Password */}
            <div className="card-clay p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Lock size={18} className="text-[var(--brand-primary)]" /><h3 className="font-bold text-sm">{lang === 'tr' ? 'Şifre Değiştir' : 'Change Password'}</h3></div>
                <button onClick={() => { setShowPwSection(!showPwSection); setPwError(''); setPwSuccess(false); }} className="text-xs font-bold text-[var(--brand-primary)]" data-testid="toggle-pw-section">
                  {showPwSection ? (lang === 'tr' ? 'Kapat' : 'Close') : (lang === 'tr' ? 'Değiştir' : 'Change')}
                </button>
              </div>
              {showPwSection && (
                <div className="space-y-3">
                  {pwSuccess && <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs font-bold text-green-700 flex items-center gap-1"><CheckCircle size={14} /> {lang === 'tr' ? 'Şifre başarıyla değiştirildi!' : 'Password changed!'}</div>}
                  {pwError && <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs font-bold text-red-700">{pwError}</div>}
                  <div>
                    <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Mevcut Şifre' : 'Current Password'}</label>
                    <div className="relative">
                      <input type={pwVisibility.current ? 'text' : 'password'} value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})} className="w-full px-3 py-2 pr-10 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" data-testid="current-password" />
                      <button type="button" onClick={() => setPwVisibility(v => ({ ...v, current: !v.current }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--brand-primary)]" aria-label={lang === 'tr' ? 'Şifreyi göster/gizle' : 'Show/hide password'} data-testid="toggle-current-password">
                        {pwVisibility.current ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Yeni Şifre' : 'New Password'}</label>
                    <div className="relative">
                      <input type={pwVisibility.new ? 'text' : 'password'} value={pwForm.newPw} onChange={e => setPwForm({...pwForm, newPw: e.target.value})} className="w-full px-3 py-2 pr-10 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" data-testid="new-password" />
                      <button type="button" onClick={() => setPwVisibility(v => ({ ...v, new: !v.new }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--brand-primary)]" aria-label={lang === 'tr' ? 'Şifreyi göster/gizle' : 'Show/hide password'} data-testid="toggle-new-password">
                        {pwVisibility.new ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Yeni Şifre Tekrar' : 'Confirm Password'}</label>
                    <div className="relative">
                      <input type={pwVisibility.confirm ? 'text' : 'password'} value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} className="w-full px-3 py-2 pr-10 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" data-testid="confirm-password" />
                      <button type="button" onClick={() => setPwVisibility(v => ({ ...v, confirm: !v.confirm }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--brand-primary)]" aria-label={lang === 'tr' ? 'Şifreyi göster/gizle' : 'Show/hide password'} data-testid="toggle-confirm-password">
                        {pwVisibility.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <button onClick={async () => {
                    setPwError(''); setPwSuccess(false);
                    if (pwForm.newPw !== pwForm.confirm) { setPwError(lang === 'tr' ? 'Şifreler uyuşmuyor' : 'Passwords do not match'); return; }
                    if (pwForm.newPw.length < 6) { setPwError(lang === 'tr' ? 'Şifre en az 6 karakter olmalı' : 'Min 6 characters'); return; }
                    setChangingPw(true);
                    try {
                      await axios.post(`${API}/profile/change-password`, { current_password: pwForm.current, new_password: pwForm.newPw }, { headers: authHeaders() });
                      setPwSuccess(true);
                      setPwForm({ current: '', newPw: '', confirm: '' });
                      toast.success(lang === 'tr' ? 'Şifre başarıyla değiştirildi' : 'Password changed successfully');
                    } catch (err) {
                      const msg = getApiErrorMessage(err, lang);
                      setPwError(msg);
                      toast.error(msg);
                    }
                    finally { setChangingPw(false); }
                  }} disabled={changingPw || !pwForm.current || !pwForm.newPw || !pwForm.confirm}
                    className="btn-primary px-6 py-2 text-sm w-full disabled:opacity-50" data-testid="change-password-btn">
                    {changingPw ? '...' : (lang === 'tr' ? 'Şifre Değiştir' : 'Change Password')}
                  </button>
                </div>
              )}
            </div>

            {/* Performance Summary */}
            {(profile?.interview_count > 0 || profile?.quiz_count > 0) && (
              <div className="card-clay p-5">
                <h3 className="font-bold text-sm mb-3">{lang === 'tr' ? 'Performans' : 'Performance'}</h3>
                <div className="grid grid-cols-3 gap-3">
                  {profile?.quiz_count > 0 && <div className="bg-green-50 rounded-xl p-3 text-center"><p className="font-bold text-lg text-green-600">{profile.quiz_count}</p><p className="text-[9px] font-bold text-[var(--text-muted)]">{lang === 'tr' ? 'Quiz Tamamlandı' : 'Quizzes Done'}</p></div>}
                  {profile?.quiz_xp > 0 && <div className="bg-yellow-50 rounded-xl p-3 text-center"><p className="font-bold text-lg text-yellow-600">{profile.quiz_xp}</p><p className="text-[9px] font-bold text-[var(--text-muted)]">Quiz XP</p></div>}
                  {profile?.interview_count > 0 && <div className="bg-sky-50 rounded-xl p-3 text-center"><p className="font-bold text-lg text-sky-600">{profile.interview_avg}%</p><p className="text-[9px] font-bold text-[var(--text-muted)]">{lang === 'tr' ? 'Mülakat Ort.' : 'Interview Avg'}</p></div>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Courses Tab */}
        {tab === 'courses' && (
          <div className="space-y-3">
            {(profile?.enrolled_courses || []).length > 0 ? (profile.enrolled_courses.map((e, i) => (
              <Link key={i} to={`/courses/${e.course_id}`} className="card-clay p-4 flex items-center gap-4 hover:border-[var(--brand-primary)] transition-colors" data-testid={`my-course-${i}`}>
                <img src={e.course_thumbnail || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=80'} alt="" className="w-16 h-11 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{lang === 'tr' ? (e.course_title_tr || e.course_title) : e.course_title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[9px] font-bold bg-sky-50 text-sky-600 px-2 py-0.5 rounded">{e.course_category}</span>
                    <span className="text-[9px] text-[var(--text-muted)]">{e.completed_lessons?.length || 0} {lang === 'tr' ? 'ders tamamlandı' : 'lessons done'}</span>
                  </div>
                </div>
                <div className="w-16 text-right">
                  <p className="font-bold text-sm text-[var(--brand-primary)]">{e.progress || 0}%</p>
                  <Progress value={e.progress || 0} className="h-1.5 mt-1" />
                </div>
                <ChevronRight size={16} className="text-[var(--text-muted)]" />
              </Link>
            ))) : (
              <div className="card-clay p-8 text-center"><BookOpen size={36} className="mx-auto text-[var(--text-muted)] mb-3" /><p className="font-bold text-sm text-[var(--text-muted)]">{lang === 'tr' ? 'Henüz kursa kayıt olmadınız' : 'No courses yet'}</p><Link to="/courses" className="btn-primary px-6 py-2 text-sm mt-3 inline-block">{lang === 'tr' ? 'Kursları Keşfet' : 'Browse Courses'}</Link></div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {tab === 'payments' && (
          <div className="space-y-3">
            {(profile?.payments || []).length > 0 ? (profile.payments.map((p, i) => (
              <div key={i} className="card-clay p-4 flex items-center gap-4" data-testid={`payment-${i}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${p.status === 'completed' ? 'bg-green-50' : 'bg-yellow-50'}`}>
                  <CreditCard size={18} className={p.status === 'completed' ? 'text-green-500' : 'text-yellow-500'} />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm">{p.course_id}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">{p.provider} - {new Date(p.created_at).toLocaleDateString('tr-TR')}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[var(--brand-primary)]">{p.amount} TRY</p>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${p.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{p.status === 'completed' ? (lang === 'tr' ? 'Tamamlandı' : 'Done') : p.status}</span>
                </div>
              </div>
            ))) : (
              <div className="card-clay p-8 text-center"><CreditCard size={36} className="mx-auto text-[var(--text-muted)] mb-3" /><p className="font-bold text-sm text-[var(--text-muted)]">{lang === 'tr' ? 'Ödeme geçmişi yok' : 'No payments yet'}</p></div>
            )}
          </div>
        )}

        {/* Notifications Tab — bildirim/iletişim tercihleri (KVKK) */}
        {tab === 'notifications' && (
          <NotificationPreferencesSection />
        )}

        {/* Privacy Tab — Dijital Rozet (Sanayi Bakanlığı) veri paylaşım rızası
            + self-servis hesap silme (mağaza yayın koşulu, bkz. DeleteAccountSection.js) */}
        {tab === 'privacy' && (
          <div className="space-y-4">
            <DijitalRozetConsentSection />
            <DeleteAccountSection />
          </div>
        )}

        {/* Certificates Tab */}
        {tab === 'certificates' && (
          <div className="space-y-3">
            {(profile?.certificates || []).length > 0 ? (profile.certificates.map((c, i) => (
              <div key={i} className="card-clay p-4 flex items-center gap-4" data-testid={`cert-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--brand-secondary)] to-yellow-300 flex items-center justify-center"><Award size={20} className="text-[var(--text-main)]" /></div>
                <div className="flex-1"><p className="font-bold text-sm">{c.course_title || c.course_id}</p><p className="text-[10px] text-[var(--text-muted)]">{new Date(c.issued_at || c.created_at).toLocaleDateString('tr-TR')}</p></div>
                {c.certificate_id && <button onClick={() => downloadCertPdf(c.certificate_id, c.course_title)} className="btn-tactile px-3 py-1.5 text-xs bg-white border-[var(--border-light)] flex items-center gap-1 cursor-pointer" data-testid={`cert-pdf-btn-${i}`}><FileText size={12} /> PDF</button>}
              </div>
            ))) : (
              <div className="card-clay p-8 text-center"><Award size={36} className="mx-auto text-[var(--text-muted)] mb-3" /><p className="font-bold text-sm text-[var(--text-muted)]">{lang === 'tr' ? 'Henüz sertifika yok' : 'No certificates yet'}</p></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
